import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const FORMSPREE = "https://formspree.io/f/xljdzpzz";

const inputClass =
  "w-full rounded-md border border-[#C4A36A]/35 bg-white px-3.5 py-3 font-serif text-[15px] text-[#1A120C] caret-[#1A120C] outline-none ring-0 placeholder:text-[#6A635A] focus:outline-none focus:ring-1 focus:ring-[#C4A36A] md:absolute md:rounded-md md:border-0 md:px-3.5 md:py-0 md:text-[13px] md:focus:ring-0 [&:-webkit-autofill]:[-webkit-text-fill-color:#1A120C] [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_#ffffff_inset]";

export const ClosingCTA = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (searchParams.get("waitlist") === "1") {
      setStatus("success");
    }
  }, [searchParams]);

  const onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setError("");
    setStatus("submitting");

    try {
      const res = await fetch(FORMSPREE, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("submit");
      try {
        window.posthog?.capture?.("waitlist_submit", { city: "Richmond", source: "landing" });
      } catch (err) {
        console.warn("Analytics unavailable", err);
      }
      setStatus("success");
      window.history.replaceState(null, "", "/?waitlist=1#waitlist");
    } catch {
      setStatus("idle");
      setError("We could not hold your place. Please try again.");
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="closing-cta-section"
      className="bg-[#FDF8F2] px-4 py-10 sm:px-8 sm:py-16 md:py-20"
    >
      <h2 className="sr-only" data-testid="closing-cta-headline">
        Elevated Concierge Waitlist
      </h2>
      <p className="sr-only" data-testid="closing-cta-subtext">
        Priority access. Personalized service.
      </p>

      <div className="relative mx-auto w-full max-w-[960px]">
        <img
          src="/assets/waitlist-section.jpg?v=3"
          alt="Elevated Concierge Waitlist. Priority access. Personalized service. Join the waitlist for bespoke concierge privileges and elevated experiences."
          className="block h-auto w-full"
        />

        {status === "success" ? (
          <div
            className="mt-5 flex items-center justify-center bg-[#F7F3EA] px-5 py-10 md:absolute md:inset-0 md:mt-0 md:py-0"
            data-testid="closing-waitlist-success"
          >
            <div className="max-w-lg text-center">
              <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-[#A08448]">Confirmed</p>
              <h3 className="mt-4 font-serif text-3xl font-medium tracking-tight text-[#1A120C] sm:text-4xl md:text-5xl">
                You&apos;re on the <span className="italic text-[#A08448]">Richmond</span> list.
              </h3>
              <span className="mx-auto mt-6 block h-px w-16 bg-[#C4A36A]" />
            </div>
          </div>
        ) : (
          <form
            id="closing-waitlist-form"
            action={FORMSPREE}
            method="POST"
            onSubmit={onSubmit}
            data-testid="closing-waitlist-form"
            className="mt-5 flex flex-col gap-3 md:absolute md:inset-0 md:mt-0 md:block md:gap-0"
          >
            <input type="hidden" name="_subject" value="Community Hearts waitlist" />

            <label htmlFor="closing-waitlist-name" className="sr-only">
              Full Name
            </label>
            <input
              id="closing-waitlist-name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Full Name"
              data-testid="closing-waitlist-name-input"
              className={`${inputClass} md:left-[51.2%] md:top-[44.25%] md:h-[5.53%] md:w-[27%]`}
            />

            <label htmlFor="closing-waitlist-email" className="sr-only">
              Email Address
            </label>
            <input
              id="closing-waitlist-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="Email Address"
              data-testid="closing-waitlist-email-input"
              className={`${inputClass} md:left-[51.2%] md:top-[53.28%] md:h-[5.53%] md:w-[27%]`}
            />

            <label htmlFor="closing-waitlist-reason" className="sr-only">
              Reason for Joining (Optional)
            </label>
            <select
              id="closing-waitlist-reason"
              name="reason"
              defaultValue=""
              data-testid="closing-waitlist-source-input"
              className={`${inputClass} cursor-pointer appearance-none !text-[#6A635A] [&:has(option:checked:not([value='']))]:!text-[#1A120C] md:left-[51.2%] md:top-[62.3%] md:h-[5.53%] md:w-[27%]`}
            >
              <option value="" style={{ color: "#6A635A", backgroundColor: "#ffffff" }}>
                Reason for Joining (Optional)
              </option>
              <option value="Membership" style={{ color: "#1A120C", backgroundColor: "#ffffff" }}>
                Membership
              </option>
              <option value="A personal introduction" style={{ color: "#1A120C", backgroundColor: "#ffffff" }}>
                A personal introduction
              </option>
              <option value="An event" style={{ color: "#1A120C", backgroundColor: "#ffffff" }}>
                An event
              </option>
              <option value="Something else" style={{ color: "#1A120C", backgroundColor: "#ffffff" }}>
                Something else
              </option>
            </select>

            <label
              className="flex items-center gap-2 bg-[#FBF6EC] px-1 py-2 text-[12px] leading-snug text-[#6F6254] md:absolute md:gap-1.5 md:px-0 md:py-0 md:text-[10px] md:leading-none md:left-[52.6%] md:top-[68.55%] md:h-[2.7%] md:w-[28.2%]"
              data-testid="closing-waitlist-age-label"
            >
              <input
                type="checkbox"
                required
                name="age_confirm"
                value="yes"
                data-testid="closing-waitlist-age-checkbox"
                className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-[#C4A36A] md:h-3 md:w-3"
              />
              <span>I confirm that I am 18 years of age or older.</span>
            </label>

            <button
              type="submit"
              disabled={status === "submitting"}
              data-testid="closing-waitlist-submit-button"
              aria-label="Join waitlist"
              className="h-12 w-full cursor-pointer rounded-md border-0 bg-[#C0965A] font-serif text-[12px] font-semibold uppercase tracking-[0.18em] text-[#FFF8E8] disabled:cursor-wait md:absolute md:h-[6.6%] md:bg-transparent md:text-transparent md:left-[51.2%] md:top-[71.6%] md:w-[31%]"
            >
              Join waitlist
            </button>
          </form>
        )}
      </div>

      {error ? (
        <p className="mt-4 text-center font-serif text-sm text-[#8C5A32]" data-testid="closing-waitlist-error">
          {error}
        </p>
      ) : null}

      <p
        className="mt-5 text-center font-serif text-sm italic text-[#A08448] sm:mt-6 sm:text-base"
        data-testid="closing-waitlist-social-proof"
      >
        Join people waiting in Richmond.
      </p>
      <p
        className="mt-2 text-center font-serif text-[13px] leading-relaxed text-[#5C4632] sm:text-sm"
        data-testid="closing-founding-note"
      >
        Early Richmond members receive a founding rate and a Gold Status badge.
      </p>
    </section>
  );
};
